import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LogoutButton from "../components/profile/Logout";
import { motion } from "framer-motion";
import PersonalInfoSection from "../components/profile/PersonalInfoSection";
import PassportSection from "../components/profile/PassportSection";
import NotificationSection from "../components/profile/NotificationSection";
import FavoritesSection from "../components/profile/FavoritesSection";
import BookingHistorySection from "../components/profile/BookingHistorySection";
import GolfingBuddiesSection from "../components/profile/GolfingBuddiesSection";
import { auth } from "../firebase";
import { getProfile, updateProfile } from "../services/profileService";
import {
  User,
  Heart,
  BookMarked,
  Bell,
  ClipboardList,
  Users,
} from "lucide-react";
import retrieveMyInfoBtn from "../assets/singpass/retrieve-myinfo-btn-stacked-primary.svg";

const TABS = [
  { value: "personal", label: "Personal Info", Icon: User },
  { value: "preferences", label: "Preferences", Icon: Heart },
  { value: "buddies", label: "Golfing Buddies", Icon: Users },
];

const PERSONAL_SUB_TABS = [
  { value: "info", label: "Personal Information", Icon: User },
  { value: "passport", label: "Passport", Icon: BookMarked },
];

const PREFERENCES_SUB_TABS = [
  { value: "favorites", label: "Favorites", Icon: Heart },
  { value: "notifications", label: "Notifications", Icon: Bell },
  { value: "history", label: "Booking History", Icon: ClipboardList },
];

/** Secondary, one-at-a-time nav used inside a main tab — switches between
 * its children instead of stacking them all on the page at once. */
function SubTabs({ tabs, active, onChange }) {
  return (
    <div className="flex gap-1 p-1 rounded-xl bg-gray-100 mb-6 w-fit overflow-x-auto max-w-full">
      {tabs.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => onChange(value)}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
            active === value
              ? "bg-white text-[#0B2421] font-semibold shadow-sm"
              : "text-gray-500 hover:text-[#0B2421]"
          }`}
        >
          <Icon className="h-3.5 w-3.5 shrink-0" />
          {label}
        </button>
      ))}
    </div>
  );
}

export default function Profile() {
  const [authUser, setAuthUser] = useState(
    /** @type {import("firebase/auth").User | null} */ (null),
  );
  const [user, setUser] = useState(/** @type {any} */ (null));
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [personalSubTab, setPersonalSubTab] = useState("info");
  const [preferencesSubTab, setPreferencesSubTab] = useState("favorites");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      setAuthUser(fbUser);
      setAuthChecked(true);
    });
    return unsub;
  }, []);

  const fetchProfile = async (uid, email) => {
    try {
      const profile = await getProfile(uid);
      setUser(profile || { email });
    } catch (err) {
      console.error("Failed to load profile:", err);
    }
  };

  useEffect(() => {
    if (!authUser) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      await fetchProfile(authUser.uid, authUser.email);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  const handleRetrieveInfo = async () => {
    if (!authUser || isRefreshing) return;
    setIsRefreshing(true);
    await fetchProfile(authUser.uid, authUser.email);
    setIsRefreshing(false);
  };

  /** @param {Record<string, any>} updates */
  const handleUpdate = async (updates) => {
    if (!authUser) throw new Error("Not authenticated.");
    await updateProfile(authUser.uid, updates);
    setUser((prev) => ({ ...prev, ...updates }));
  };

  const TabsTriggerAny = /** @type {React.ComponentType<any>} */ (TabsTrigger);
  const TabsListAny = /** @type {React.ComponentType<any>} */ (TabsList);
  const TabsContentAny = /** @type {React.ComponentType<any>} */ (TabsContent);

  if (!authChecked || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <span className="text-[#D4AF37] font-bold text-xl tracking-widest">
            EXQURA
          </span>
          <div className="w-5 h-5 border-2 border-[#0B2421] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-[#0B2421] font-semibold">
          Please sign in to view your profile.
        </p>
      </div>
    );
  }

  const displayName = user?.fullName || authUser.displayName || "Golfer";
  const initials = displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const photoUrl = user?.profile_photo_url || authUser.photoURL || null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Identity bar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="flex items-center gap-4"
          >
            {/* Avatar + name */}
            <div className="flex items-start gap-4">
              <div className="relative shrink-0">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={displayName}
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-[#D4AF37] ring-offset-2"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#0B2421] flex items-center justify-center ring-2 ring-[#D4AF37] ring-offset-2">
                    <span className="text-[#D4AF37] font-bold text-xl">
                      {initials}
                    </span>
                  </div>
                )}
                <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white" />
              </div>

              <div>
                <p className="text-xs font-semibold text-[#D4AF37] uppercase tracking-widest mb-0.5">
                  My Account
                </p>
                <h1 className="text-[#0B2421] font-bold text-2xl leading-tight">
                  {displayName}
                </h1>
                <p className="text-gray-400 text-sm mb-3">
                  {user?.email || authUser.email}
                </p>

                {/* Official Singpass "Retrieve MyInfo" button asset (stacked,
                    primary/red variant) — per Singpass brand guidelines this
                    logo must not be edited, recoloured, or reconstructed
                    with custom text/icons, so it's rendered as-is and only
                    resized via CSS. Still just re-syncs the locally stored
                    profile for now — real MyInfo retrieval needs a
                    registered Singpass Relying Party client + backend
                    callback, which isn't wired up yet. */}
                {/* <button
                  type="button"
                  onClick={handleRetrieveInfo}
                  disabled={isRefreshing}
                  className="inline-block rounded-lg shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  <img
                    src={retrieveMyInfoBtn}
                    alt="Retrieve MyInfo with Singpass"
                    className="h-12 w-auto"
                  />
                </button> */}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1 }}
        >
          <Tabs defaultValue="personal">
            {/* Tab bar */}
            <TabsListAny className="flex h-auto gap-1 p-1 rounded-2xl bg-white border border-gray-100 shadow-sm mb-6 overflow-x-auto">
              {TABS.map(({ value, label, Icon }) => (
                <TabsTriggerAny
                  key={value}
                  value={value}
                  className="
                    flex items-center gap-2 flex-1 min-w-max px-4 py-2.5 rounded-xl text-sm font-medium
                    text-gray-500 transition-all whitespace-nowrap
                    data-[state=active]:bg-[#0B2421] data-[state=active]:text-[#D4AF37] data-[state=active]:font-semibold data-[state=active]:shadow-sm
                    hover:text-[#0B2421] hover:bg-gray-50
                  "
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                </TabsTriggerAny>
              ))}
            </TabsListAny>

            <TabsContentAny value="personal">
              <SubTabs
                tabs={PERSONAL_SUB_TABS}
                active={personalSubTab}
                onChange={setPersonalSubTab}
              />
              {personalSubTab === "info" ? (
                <PersonalInfoSection
                  user={user}
                  uid={authUser.uid}
                  onUpdate={handleUpdate}
                />
              ) : (
                <PassportSection
                  user={user}
                  uid={authUser.uid}
                  onUpdate={handleUpdate}
                />
              )}
            </TabsContentAny>
            <TabsContentAny value="preferences">
              <SubTabs
                tabs={PREFERENCES_SUB_TABS}
                active={preferencesSubTab}
                onChange={setPreferencesSubTab}
              />
              {preferencesSubTab === "favorites" && <FavoritesSection />}
              {preferencesSubTab === "notifications" && (
                <NotificationSection user={user} onUpdate={handleUpdate} />
              )}
              {preferencesSubTab === "history" && <BookingHistorySection />}
            </TabsContentAny>
            <TabsContentAny value="buddies">
              <GolfingBuddiesSection uid={authUser.uid} />
            </TabsContentAny>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
